/**
 * How an uploaded file is presented in a list row - the extension badge, the
 * label it is listed under, and the upload time.
 *
 * Shared by the order Uploads tab and the company diary's Files tab so the two
 * cannot drift into different badge colours for the same file type.
 */

/** One upload in flight, as every file list tracks it. */
export interface PendingFile {
    id: string | number;
    /** Kept so a failed upload can be retried without re-picking the file. */
    file: File;
    name: string;
    status: 'preparing' | 'uploading' | 'error';
    loaded: number;
    total: number;
    percent: number;
}

interface DisplayableFile {
    name?: string | null;
    file_name?: string | null;
    collection_name?: string | null;
    custom_properties?: Record<string, any> | null;
    mime_type?: string | null;
    created_at?: string | null;
    url?: string | null;
    is_image?: boolean | null;
    thumb_url?: string | null;
    preview_url?: string | null;
    preview_failure?: string | null;
}

/** The types the API builds thumbnails and previews for, so both sides agree on what an image is. */
export const IMAGE_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
    'image/x-adobe-dng',
    'image/dng',
];

/** Images only the API's thumb and preview can show: HEIC draws in Safari alone, and a raw DNG in no browser. */
const UNDRAWABLE_IMAGE_MIME_TYPES = ['image/heic', 'image/heif', 'image/x-adobe-dng', 'image/dng'];
const UNDRAWABLE_IMAGE_EXTENSIONS = ['heic', 'heif', 'dng'];

/** How long a fresh upload shows a skeleton while its thumbnail is generated. */
export const THUMBNAIL_PENDING_MS = 2 * 60 * 1000;

export type ThumbnailSource =
    | { kind: 'thumb'; src: string }
    | { kind: 'failed'; label: string }
    | { kind: 'pending' }
    | { kind: 'original'; src: string }
    | { kind: 'icon' };

export interface PreviewFailureText {
    /** Short, for a card or a row. */
    label: string;
    /** A sentence, for the viewer. */
    message: string;
}

/** Why the API made no thumb or preview for a file it found at fault, by its `preview_failure`. */
const PREVIEW_FAILURE_TEXT: Record<string, PreviewFailureText> = {
    corrupt: { label: 'Corrupted', message: "This file is corrupted and can't be previewed." },
    empty: { label: 'Empty file', message: "This file is empty and can't be previewed." },
    too_large: { label: 'Too large to preview', message: 'This image is too large to preview.' },
    no_embedded_preview: { label: 'No preview in DNG', message: 'This DNG has no preview image inside it.' },
};

const UNKNOWN_PREVIEW_FAILURE: PreviewFailureText = {
    label: "Can't preview",
    message: "This file can't be previewed.",
};

/** What to say about a file the API couldn't preview, or null when there's nothing to say. */
export function previewFailure(file: DisplayableFile): PreviewFailureText | null {
    // An image over the queue's decode budget is still an original the browser can draw.
    if (!file.preview_failure || (file.preview_failure === 'too_large' && !needsImagePreview(file))) {
        return null;
    }

    return PREVIEW_FAILURE_TEXT[file.preview_failure] ?? UNKNOWN_PREVIEW_FAILURE;
}

/** The API's own verdict when it sends one, otherwise the MIME type. */
export function isImageFile(file: DisplayableFile): boolean {
    if (typeof file.is_image === 'boolean') {
        return file.is_image;
    }

    return IMAGE_MIME_TYPES.includes((file.mime_type || '').toLowerCase());
}

/** By extension too: iOS can upload a HEIC as `application/octet-stream`, and a DNG reads as a TIFF. */
export function needsImagePreview(file: DisplayableFile): boolean {
    const mimeType = (file.mime_type || '').toLowerCase();

    return (
        UNDRAWABLE_IMAGE_MIME_TYPES.includes(mimeType) ||
        UNDRAWABLE_IMAGE_EXTENSIONS.includes(getFileExtension(file) ?? '')
    );
}

/**
 * What fills a file's thumbnail slot. A missing `thumb_url` key means the API
 * builds no thumbnails yet, so nothing is waited on.
 */
export function thumbnailSource(
    file: DisplayableFile,
    now: number = Date.now(),
    /** False where `url` points at a private object, which only a signed link can read. */
    originalsReadable: boolean = true,
): ThumbnailSource {
    if (!isImageFile(file)) {
        return { kind: 'icon' };
    }

    if (file.thumb_url) {
        return { kind: 'thumb', src: file.thumb_url };
    }

    // The original would only fail to load too.
    const failure = previewFailure(file);

    if (failure) {
        return { kind: 'failed', label: failure.label };
    }

    const uploadedAt = file.created_at ? new Date(file.created_at).getTime() : NaN;

    if (
        !file.preview_failure &&
        'thumb_url' in file &&
        Number.isFinite(uploadedAt) &&
        now - uploadedAt < THUMBNAIL_PENDING_MS
    ) {
        return { kind: 'pending' };
    }

    if (originalsReadable && !needsImagePreview(file) && file.url) {
        return { kind: 'original', src: file.url };
    }

    return { kind: 'icon' };
}

export type PreviewKind = 'image' | 'video' | 'pdf' | 'text';

const VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const VIDEO_EXTENSIONS = ['mp4', 'm4v', 'mov', 'webm'];

/** Served by the API as plain text for preview, whatever type they were uploaded with. */
const TEXT_MIME_TYPES = ['text/plain', 'text/csv', 'application/json', 'text/markdown', 'text/x-markdown'];
const TEXT_EXTENSIONS = ['txt', 'csv', 'log', 'json', 'md', 'markdown'];

/** How the file viewer shows a file, or null when it can only show the file's icon and name. */
export function previewKind(file: DisplayableFile): PreviewKind | null {
    const mimeType = (file.mime_type || '').toLowerCase();
    const extension = getFileExtension(file) ?? '';

    if (isImageFile(file)) {
        if (file.preview_url) {
            return 'image';
        }

        // A HEIC or a DNG can't be drawn from the original until the API has built its preview, nor can a broken file.
        return previewFailure(file) || needsImagePreview(file) ? null : 'image';
    }

    if (VIDEO_MIME_TYPES.includes(mimeType) || VIDEO_EXTENSIONS.includes(extension)) {
        return 'video';
    }

    if (mimeType === 'application/pdf' || extension === 'pdf') {
        return 'pdf';
    }

    if (TEXT_MIME_TYPES.includes(mimeType) || TEXT_EXTENSIONS.includes(extension)) {
        return 'text';
    }

    return null;
}

/** The file's extension, lowercased, or null when it has none. */
export function getFileExtension(file: DisplayableFile): string | null {
    const fileName = file.file_name || file.name || '';
    const lastDot = fileName.lastIndexOf('.');

    if (lastDot === -1 || lastDot === fileName.length - 1) {
        return null;
    }

    return fileName.slice(lastDot + 1).toLowerCase();
}

/** Background class for the extension badge sitting on the document icon. */
export function getExtensionBgClass(extension: string | null | undefined): string {
    switch ((extension || '').toLowerCase()) {
        case 'pdf':
            return 'bg-muval-red-1';
        case 'jpg':
        case 'jpeg':
            return 'bg-muval-blue-1';
        case 'png':
            return 'bg-muval-purple-1';
        default:
            return 'bg-muval-gray-3';
    }
}

/**
 * The label to list the file under: the operator's own label first, then the
 * uploader's description, then the stored name.
 */
export function getFileDisplayName(file: DisplayableFile): string {
    return file.custom_properties?.display_label || file.custom_properties?.info || file.file_name || file.name || '';
}

/** Human-readable byte count for an in-flight upload. */
export function formatBytes(bytes: number): string {
    if (!bytes) {
        return '0 B';
    }

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}
